import { z } from "zod";
import { ProviderId, SiteId, VehicleType } from "./common.js";
import { CountByDocumentStatus, DriverId, Eligibility, LocalVehicleId, RosterCode, RosterHolder } from "./drivers.js";
import { OperatingDate } from "./ride-requests.js";
import { RequiredDocuments } from "./site-parameters.js";
// --- Nomina unica multiplanta del proveedor (PP-70, ADR-025) --------------------
// La nomina es de la empresa madre y la habilitacion es por planta (ADR-014):
// cada vehiculo y cada conductor viene una sola vez, con una entrada por planta
// habilitada donde tiene fila (su id local, el bloqueo de la planta y la
// elegibilidad para esa planta). Mismo conjunto de plantas que /provider/trips:
// sale de la base en cada request, nunca de un claim, un header, el body ni la
// query. Nada de otra empresa madre.
// en castellano: habilitacion del integrante en la planta (enablement).
export const ProviderRosterQuery = z
    .object({})
    .strict()
    .openapi("ProviderRosterQuery", { description: "Sin parametros: ni planta, ni proveedor, ni organizacion (400). El conjunto de plantas sale de la base." });
export const ProviderRosterSite = z
    .object({
    siteId: SiteId,
    providerId: ProviderId.openapi({ description: "Id local de la empresa madre en esa planta (su habilitacion)." }),
    siteName: z.string().min(1).openapi({ description: "Nombre comercial de la planta." }),
    timezone: z.string().min(1).openapi({ description: "Zona IANA de la planta." }),
    today: OperatingDate.openapi({ description: "Fecha operativa de la planta con la que se calculo la elegibilidad en ella." }),
    requiredDocuments: RequiredDocuments.openapi({ description: "Tipos que la planta exige a vehiculos y conductores." }),
    vehicleMaxAgeYears: z.number().int().nonnegative().optional().openapi({
        format: "int32",
        description: "Antiguedad maxima que la planta pone a los vehiculos. Ausente: sin condicion.",
    }),
})
    .strict()
    .openapi("ProviderRosterSite", { description: "Planta incluida en la lectura: una por habilitacion activa de la empresa madre." });
const enablementFields = {
    siteId: SiteId,
    providerId: ProviderId,
    active: z.boolean().openapi({ description: "Bloqueo de la planta: false cuando la planta lo bloqueo. Lo decide la planta." }),
    eligibility: Eligibility,
};
export const ProviderRosterVehicleEnablement = z
    .object({ ...enablementFields, id: LocalVehicleId.openapi({ description: "Id local del vehiculo en esa planta: el de los viajes y las escrituras por planta." }) })
    .strict()
    .openapi("ProviderRosterVehicleEnablement", { description: "Un vehiculo de la nomina visto desde una planta habilitada: id local, bloqueo y elegibilidad para esa planta." });
export const ProviderRosterDriverEnablement = z
    .object({ ...enablementFields, id: DriverId.openapi({ description: "Id local del conductor en esa planta." }) })
    .strict()
    .openapi("ProviderRosterDriverEnablement", { description: "Un conductor de la nomina visto desde una planta habilitada: id local, bloqueo y elegibilidad para esa planta." });
const documentsField = CountByDocumentStatus.openapi({
    description: "Documentos del vehiculo o la persona (se cargan una sola vez y valen en todas las plantas), por estado a la fecha operativa mas tardia de las plantas (today de la respuesta). Lo que falta para cada planta esta en su elegibilidad.",
});
const inRosterField = z.boolean().openapi({
    description: "false cuando la empresa madre lo dio de baja de su nomina (en todas sus plantas). Vuelve a true con la reincorporacion.",
});
export const ProviderRosterVehicle = z
    .object({
    code: RosterCode,
    type: VehicleType,
    capacity: z.number().int().positive().openapi({ format: "int32", description: "Pasajeros sentados, sin contar al chofer." }),
    plate: z.string().min(1),
    year: z.number().int().min(1950).max(2100).optional().openapi({ format: "int32", description: "Anio del modelo. Ausente: no cargado." }),
    holder: RosterHolder,
    inRoster: inRosterField,
    documents: documentsField,
    sites: z.array(ProviderRosterVehicleEnablement).openapi({ description: "Una entrada por planta habilitada donde tiene fila, en el orden de sites." }),
})
    .strict()
    .openapi("ProviderRosterVehicle", { description: "Vehiculo de la nomina de la empresa madre, una sola vez, con su elegibilidad en cada planta habilitada. Sin ids globales." });
export const ProviderRosterDriver = z
    .object({
    code: RosterCode,
    name: z.string().min(1),
    idDocument: z.string().min(1).optional().openapi({ description: "Identificador que la empresa usa para el conductor, para el formulario de edicion. Ausente si no lo cargo." }),
    phone: z.string().min(1).optional().openapi({ description: "Telefono de contacto, para el formulario de edicion. Ausente si no lo cargo." }),
    holder: RosterHolder,
    inRoster: inRosterField,
    documents: documentsField,
    sites: z.array(ProviderRosterDriverEnablement).openapi({ description: "Una entrada por planta habilitada donde tiene fila, en el orden de sites." }),
})
    .strict()
    .openapi("ProviderRosterDriver", { description: "Conductor de la nomina de la empresa madre, una sola vez, con su elegibilidad en cada planta habilitada. Sin ids globales." });
export const ProviderRosterResponse = z
    .object({
    today: OperatingDate.optional().openapi({ description: "Fecha operativa mas tardia de las plantas: con ella se cuentan los documentos por estado. Ausente si no hay plantas." }),
    sites: z.array(ProviderRosterSite).openapi({ description: "Plantas habilitadas, por nombre." }),
    vehicles: z.array(ProviderRosterVehicle).openapi({ description: "Primero los que estan en la nomina, despues las bajas; por codigo de movil." }),
    drivers: z.array(ProviderRosterDriver).openapi({ description: "Mismo orden que vehicles." }),
})
    .strict()
    .openapi("ProviderRosterResponse", { description: "Nomina de la empresa madre en todas sus plantas habilitadas. Sin habilitaciones: listas vacias, no error." });
//# sourceMappingURL=provider-roster.js.map